import React, { useCallback, useEffect, useRef, useState } from 'react';

import { IconTypes } from '@eika/icon';
import { ErrorLabel } from '@eika/label';

import { useConfig } from '../../../defaultConfig';
import { paymentFromSelector, useAccounts } from '../../../fetchHooks/useAccount';
import useAccountChange from '../../../fetchHooks/useAccountChange';
import { useImmediatePaymentCreditor, useOcrMutation } from '../../../fetchHooks/useOcr';
import { useCreateOutboxPayment, useOutboxQuery } from '../../../fetchHooks/useOutbox';
import type { Account, AccountId, ApprovalStatus } from '../../../generated-types';
import { usePaymentAccountContext } from '../../../hooks/useAccountState';
import useShowPaymentResult from '../../../hooks/useShowPaymentResult';
import useStepUp from '../../../hooks/useStepUp';
import useTranslation from '../../../hooks/useTranslation';
import { isValidAccountPaymentFrom } from '../../../services/accountFilters';
import SigningContextFlow from '../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../signingContext/signingContext';
import FormApproveSection from '../../NettbankCreateTransactions/components/NettbankPaymentForm/FormApproveSection';
import { TransactionForm, TransactionSubmitWrapper } from '../../TransactionForm';
import { DismissKeyboardStickyFooter } from '../../shared/DismissKeyboardStickyFooter';
import AccountsEmptyInfo from '../AccountsEmptyInfo';
import { PaymentAndTransferFormTabs } from '../PaymentAndTransferForm';
import { PaymentRetryContext } from '../PaymentRetry';
import toSelectedReceiver from '../SelectedReceiver';
import InvalidFromAccountInfo, { TransactionType } from '../Transfer/InvalidFromAccountInfo';
import PaymentFields from './PaymentFields';
import { PaymentFormResult, StepUpRequired } from './components';
import { PaymentActionType } from './form/actions';
import createPaymentRequest from './form/createPaymentRequest';
import useFormContext from './form/useFormContext';
import usePaymentOutbox from './hooks/usePaymentOutbox';

import '../TransactionModal.scss';

export const hasSigningError = (paymentStatus?: ApprovalStatus): boolean => {
    return paymentStatus === 'REJECTED' || paymentStatus === 'UNKNOWN';
};

export interface Props {
    active?: boolean;
    onClose: () => void;
    onClearPayment: () => void;
    account?: Account;
    title?: string;
    lockNote?: boolean;
    lockReceiver?: boolean;
    shouldFocusFirstElement?: boolean;
    showAvtalegiroSuggestion?: (value: boolean) => void;
    isInModal?: boolean;
    useTitle?: boolean;
    useDesktopPlaceholder?: boolean;
    inFocusModal?: boolean;
    noTabs?: boolean;
}

const PaymentForm: React.FC<Props> = ({
    active = true,
    onClose,
    onClearPayment,
    account,
    title,
    lockNote,
    lockReceiver,
    shouldFocusFirstElement = false,
    showAvtalegiroSuggestion,
    isInModal = true,
    useTitle = false,
    useDesktopPlaceholder = true,
    inFocusModal = false,
    noTabs = false,
}) => {
    const translated = useTranslation();
    const {
        featureToggles: { disableAvtaleGiroChanges, disableAvtalegiro },
    } = useConfig();

    const [showStepUp, setShowStepUp] = useState(false);
    const { state, handleChange, checkFormValidity, redirectLinkAvtalegiro, curryChange, validateIsLoading } =
        useFormContext();
    const { isHighAssurance, showStepupConfiguration } = useStepUp([!!state.immediate]);
    const { targetFromAccount } = usePaymentAccountContext();
    const {
        startSigning,
        signingOperationPending,
        showSigningFlow,
        showPaymentRetry,
        trackSigningEvent,
        signingCompleted,
    } = useSigningContext(createPaymentRequest());

    useEffect(() => {
        if (account?.accountNumber) {
            handleChange({
                type: PaymentActionType.FROM_ACCOUNT_SELECTED,
                payload: account.accountNumber,
            });
        }
    }, [account?.accountNumber, handleChange]);

    const accountRefresh = useAccountChange();
    const successRef = useRef((accountId: AccountId) => accountRefresh([accountId]));
    useEffect(() => {
        if (signingCompleted && targetFromAccount) {
            successRef.current(targetFromAccount.accountId);
        }
    }, [successRef, targetFromAccount, signingCompleted]);

    const { data: accounts, ...accountState } = useAccounts({ select: paymentFromSelector });
    const { isPostingOutboxItem, onCreateOutboxPayment } = usePaymentOutbox(state, checkFormValidity, onClearPayment);
    const mutateOutbox = useCreateOutboxPayment();
    useImmediatePaymentCreditor(state.toAccount?.accountNumber, state.fromAccount, isHighAssurance);
    const { error: outboxError } = useOutboxQuery();
    const ocr = useOcrMutation(
        (data, variables) =>
            handleChange({
                type: PaymentActionType.TO_ACCOUNT_SELECTED,
                payload: toSelectedReceiver(variables, data),
            }),
        () =>
            handleChange({
                type: PaymentActionType.TO_ACCOUNT_SELECTED,
                payload: undefined,
            }),
    );

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (showStepupConfiguration) {
                setShowStepUp(true);
                return;
            }
            if (await checkFormValidity()) {
                trackSigningEvent('create_payment');
                startSigning(state);
            } else {
                trackSigningEvent('validation_error');
            }
        },
        [showStepupConfiguration, checkFormValidity, trackSigningEvent, startSigning, state],
    );

    useEffect(() => {
        const disableAvtalegiroSuggestion = !redirectLinkAvtalegiro || disableAvtalegiro || disableAvtaleGiroChanges;
        showAvtalegiroSuggestion?.(disableAvtalegiroSuggestion);
    }, [showAvtalegiroSuggestion, redirectLinkAvtalegiro, disableAvtaleGiroChanges, disableAvtalegiro]);

    const showPaymentResult = useShowPaymentResult(!!mutateOutbox.error);

    if (showStepUp) {
        return (
            <StepUpRequired
                onClose={() => {
                    setShowStepUp(false);
                }}
                onUndo={() => {
                    curryChange<boolean>(PaymentActionType.SET_IMMEDIATE)(false);
                    setShowStepUp(false);
                }}
            />
        );
    }

    if (showPaymentRetry) {
        return <PaymentRetryContext />;
    }

    if (showPaymentResult) {
        return <PaymentFormResult onClose={onClose} account={account} />;
    }

    const isInvalidAccount = !!state.fromAccount && targetFromAccount && !isValidAccountPaymentFrom(targetFromAccount);
    if (isInvalidAccount && !state.showFromAccounts) {
        return (
            <InvalidFromAccountInfo
                onSelect={() => handleChange({ type: PaymentActionType.SHOW_FROM_ACCOUNTS })}
                account={targetFromAccount}
                transactionType={TransactionType.PAYMENT}
            />
        );
    }

    if (accountState.isFetched && !accounts?.length) {
        return <AccountsEmptyInfo />;
    }

    const isSigningOrLoading = showSigningFlow || signingOperationPending || validateIsLoading;
    const isApproveDisabled = ocr.isPending || isSigningOrLoading || isPostingOutboxItem;

    return (
        <SigningContextFlow onClosePayment={onClose}>
            {!noTabs && <PaymentAndTransferFormTabs />}
            {useTitle && (
                <div className="transaction-form__header-wrapper">
                    <h2 className="title">{title ?? translated.payment}</h2>
                </div>
            )}
            <TransactionForm active={active} onSubmit={onSubmit}>
                <PaymentFields
                    isActive={active}
                    lockNote={lockNote}
                    lockReceiver={lockReceiver}
                    shouldFocusFirstElement={shouldFocusFirstElement}
                    useDesktopPlaceholder={useDesktopPlaceholder}
                    isInModal={isInModal}
                    locked={isSigningOrLoading || isPostingOutboxItem}
                    inFocusModal={inFocusModal}
                    showInstant
                />
                {outboxError && !active && (
                    <ErrorLabel
                        severe
                        icon={IconTypes.UTROPSTEGN_24}
                        className="transaction-form__error-label-outbox"
                        text={translated.commonErrors.paymentAndNextSalaryFailed}
                    />
                )}
                {active && (
                    <TransactionSubmitWrapper isInModal={isInModal} className="transaction-form__submit-container">
                        <FormApproveSection
                            state={state}
                            isApproveDisabled={isApproveDisabled}
                            onAddPaymentToApprovalOutbox={onCreateOutboxPayment}
                            paymentOperationPending={isSigningOrLoading}
                            isPostingOutboxItem={isPostingOutboxItem}
                        />
                    </TransactionSubmitWrapper>
                )}
            </TransactionForm>
            {isInModal && <DismissKeyboardStickyFooter />}
        </SigningContextFlow>
    );
};

export default PaymentForm;
